import {
  BAY_HIRE_BFF_BASE_URL,
  BAY_HIRE_BFF_SERVICE_SEGMENT,
  NOTIF_SERVICE_SEGMENT,
  NOTIFICATION_SERVICE_BASE_URL,
  USER_SERVICE_BASE_URL,
  USER_SERVICE_SEGMENT,
} from "@config/apiConfig";
import { trackApiCall } from "@telemetry/trackApiCall";
import type { AxiosError, AxiosRequestConfig } from "axios";
import { http } from "./http";

export type ApiRequestConfig = AxiosRequestConfig & {
  transactionId?: string;
};

/**
 * Optional per-service base URLs.
 * Keep this map if you later want to route specific service keys
 * to dedicated base URLs.
 *
 * NOTE: do not hardcode env-specific hosts (e.g. localhost) here - every
 * service must resolve through VITE_API_BASE_URL (see apiConfig.ts) so the
 * same bundle works across dev/test/staging/prod, UNLESS it has its own
 * dedicated override (like Bay Hire's BAY_HIRE_BFF_BASE_URL below). To
 * point only Bay Hire at a local BFF, set VITE_BAY_HIRE_BFF_BASE_URL in a
 * local, git-ignored .env override (e.g. .env.development.local) instead -
 * every other service keeps using the shared VITE_API_BASE_URL.
 */
export const SERVICE_BASES: Record<string, string | undefined> = {
  ...(BAY_HIRE_BFF_BASE_URL ? { [BAY_HIRE_BFF_SERVICE_SEGMENT]: BAY_HIRE_BFF_BASE_URL } : {}),
  ...(USER_SERVICE_BASE_URL ? { [USER_SERVICE_SEGMENT]: USER_SERVICE_BASE_URL } : {}),
  ...(NOTIFICATION_SERVICE_BASE_URL
    ? { [NOTIF_SERVICE_SEGMENT]: NOTIFICATION_SERVICE_BASE_URL }
    : {}),
};

/**
 * Legacy helper kept for backward compatibility with existing imports.
 * Builds "/{service}/{path}" relative URL (old behavior).
 */
export function svcUrl(service: string, path = ""): string {
  const s = String(service || "").replace(/^\/|\/$/g, "");
  const p = String(path || "").replace(/^\//, "");
  return `/${s}${p ? `/${p}` : ""}`;
}

/**
 * Internal: build the final request URL and config while preserving old behavior.
 * - If `service` is a full URL => return absolute URL (no baseURL override).
 * - If `service` matches SERVICE_BASES => send path relative to that baseURL.
 * - Otherwise => use the legacy "/{service}/{path}" form (no changes vs before).
 */
export function buildUrlAndConfig(
  service: string,
  path?: string,
  config?: ApiRequestConfig,
): { url: string; cfg?: ApiRequestConfig } {
  const sRaw = String(service || "").trim();
  const p = String(path || "").replace(/^\//, "");

  // 1) Absolute service (rare, but supported)
  if (/^https?:\/\//i.test(sRaw)) {
    const absolute = sRaw.replace(/\/$/, "");
    return { url: `${absolute}${p ? `/${p}` : ""}`, cfg: config };
  }

  // 2) Known mapped service -> use its dedicated baseURL and omit the service segment from the path
  const mappedBase = SERVICE_BASES[sRaw];
  if (mappedBase) {
    const cfg: AxiosRequestConfig = { ...(config || {}), baseURL: mappedBase };
    return { url: p ? `/${p}` : "/", cfg };
  }

  // 3) Default (legacy) behavior -> let http.ts baseURL handle the host
  return { url: svcUrl(sRaw, p), cfg: config };
}

function getFullUrl(url: string, cfg?: AxiosRequestConfig): string {
  const baseURL = cfg?.baseURL ?? http.defaults?.baseURL ?? "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${String(baseURL).replace(/\/$/, "")}${url}`;
}

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message?: unknown }).message ?? "Unknown error");
  }
  return "Unknown error";
}

function getStatusCode(error: unknown): number | undefined {
  const axiosError = error as AxiosError | undefined;
  return axiosError?.response?.status;
}

async function trackedRequest<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  service: string,
  path: string,
  execute: () => Promise<{ data: T; status: number; config?: ApiRequestConfig }>,
  cfg?: ApiRequestConfig,
): Promise<T> {
  const startedAt = performance.now();
  const builtUrl = buildUrlAndConfig(service, path, cfg);
  const fullUrl = getFullUrl(builtUrl.url, builtUrl.cfg);
  const transactionId = builtUrl.cfg?.transactionId;

  try {
    const response = await execute();
    const durationMs = performance.now() - startedAt;

    trackApiCall({
      service,
      path,
      method,
      url: fullUrl,
      durationMs,
      success: true,
      statusCode: response.status,
      ...(transactionId ? { transactionId } : {}),
    });

    return response.data;
  } catch (error) {
    const durationMs = performance.now() - startedAt;

    trackApiCall({
      service,
      path,
      method,
      url: fullUrl,
      durationMs,
      success: false,
      statusCode: getStatusCode(error),
      errorMessage: getErrorMessage(error),
      ...(transactionId ? { transactionId } : {}),
    });

    throw error;
  }
}

export const api = {
  get<T = unknown>(service: string, path = "", config?: ApiRequestConfig) {
    const { url, cfg } = buildUrlAndConfig(service, path, config);
    return trackedRequest<T>("GET", service, path, () => http.get<T>(url, cfg), config);
  },

  delete<T = unknown>(service: string, path = "", config?: ApiRequestConfig) {
    const { url, cfg } = buildUrlAndConfig(service, path, config);
    return trackedRequest<T>("DELETE", service, path, () => http.delete<T>(url, cfg), config);
  },

  post<T = unknown>(service: string, path = "", body?: any, config?: ApiRequestConfig) {
    const { url, cfg } = buildUrlAndConfig(service, path, config);
    return trackedRequest<T>("POST", service, path, () => http.post<T>(url, body, cfg), config);
  },

  put<T = unknown>(service: string, path = "", body?: any, config?: ApiRequestConfig) {
    const { url, cfg } = buildUrlAndConfig(service, path, config);
    return trackedRequest<T>("PUT", service, path, () => http.put<T>(url, body, cfg), config);
  },

  patch<T = unknown>(service: string, path = "", body?: any, config?: ApiRequestConfig) {
    const { url, cfg } = buildUrlAndConfig(service, path, config);
    return trackedRequest<T>("PATCH", service, path, () => http.patch<T>(url, body, cfg), config);
  },
};
